import {
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSliderChange } from '@angular/material/slider';
import { UntypedFormGroup } from '@angular/forms';
import { FloorObservationDetailsPopupComponent } from '../floor-observation-details-popup/floor-observation-details-popup.component';
import { Project360Service } from '../../../p-dashboard/project360.service';
import { GStatic } from '../../../../services/GStatic';
import { GAnalyticsService } from '../../../../services/g-analytics.service';
import { AccessLevel } from '../../../../core/user/user.model';
import { TranslocoService } from '@ngneat/transloco';
import { PointImage, ProjectFloorPoint, Report } from '../../../../models/project.model';
import { PanosService, statusToIconKey } from '../../panos.service';
import { REPORT_TYPE } from '../../const';
import { ToastrService } from 'ngx-toastr';
import {
  ConfirmDialogComponent,
  ConfirmDialogModel,
} from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { AdjustPath } from '../../../operation-section/adjust-path';
import { PendingReportsService } from '../../../operation-section/pending-reports.service';
import { SvgScriptLoaderService } from '../../../../services/SVG/svg-script-loader.service';
import { NavigationService } from '../navigation/navigation.service';

@Component({
  selector: 'app-floor-popup',
  templateUrl: './floor-popup.component.html',
  styleUrls: [
    './floor-popup.component.scss',
    '../../../new-project/new-floor/new-floor.component.scss',
  ],
})
export class FloorPopupComponent implements OnInit {
  ratioBtwImgs: { w?: number; h?: number };
  selectedReport: Report | any;
  @ViewChild('floorImgElm') floorImgElm: ElementRef;
  @ViewChild('dragableImage') dragableImage: ElementRef<HTMLElement>;
  @ViewChild('fileAttached') fileAttached: ElementRef;
  @ViewChild('imgContainer') imgContainer: any;
  @ViewChild('pointList') pointList: any;
  imageChangedEvent: any;
  floorImg: any = '';
  imgDim: {
    realH: number;
    realW: number;
    displayH: number;
    displayW: number;
    ratioW: number;
    ratioH: number;
  } = {
    displayH: 0,
    displayW: 0,
    realH: 0,
    realW: 0,
    ratioW: 0,
    ratioH: 0,
  };
  contDim: { displayH: number; displayW: number } = {
    displayW: 0,
    displayH: 0,
  };
  imgScaleDefault: number;
  pointsList: any[] = [];
  newFloorForm: UntypedFormGroup;
  projectId: any;
  floorId: any;
  pageMode: 'view' | 'edit' | 'create' = 'create';
  selectedFloor: any;
  mousePos: { x: number; y: number } = { x: 0, y: 0 };
  imgScale: number = 51;
  dragPosition: { x: number; y: number } = { x: 50, y: 50 };
  isLoading: boolean = false;
  observationsList: any[] = [];
  points: any = [];
  canAddObservation: boolean = false;
  showHideObservations: boolean = false;
  //temp code for operation engineer
  isAdjustmentMode: boolean = false;
  adjustPathObj: any;
  protected readonly REPORT_TYPE = REPORT_TYPE;
  private timerInterval: any[] = [];
  prevImgDim: {
    realH: number;
    realW: number;
    displayH: number;
    displayW: number;
    ratioW: number;
    ratioH: number;
  } = {
    displayH: 0,
    displayW: 0,
    realH: 0,
    realW: 0,
    ratioW: 0,
    ratioH: 0,
  };
  isVideoReport: boolean = false;

  //temp code for operation engineer

  constructor(
    public dialog: MatDialog,
    public _pano: PanosService,
    public _transloco: TranslocoService,
    public dialogRef: MatDialogRef<FloorPopupComponent>,
    private _toaster: ToastrService,
    public _project: Project360Service,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public googleServices: GAnalyticsService,
    private pendingReportsService: PendingReportsService,
    private scriptloader: SvgScriptLoaderService,
    private _navigationService: NavigationService,
  ) {}

  @HostListener('window:resize')
  onResize(): void {
    this.fixDimension();
  }

  async ngOnInit(): Promise<void> {
    this.isLoading = true;
    await this.prepareImage();
    if (!this._project.isOperationEngineer()) {
      if (this.data.type !== 'points') {
        let floorObservation = await this._project
          .fetchFloorObservations(this._project.selectedFloor.id, this._project.projectInfo.id)
          .catch((error) => {
            this._toaster.error(error.error.message);
          });

        const viewPrivateObservationsOnFloorPlan = this.getViewPrivateObservationsOnFloorPlan();
        const viewPublicObservationsOnFloorPlan = this.getViewPublicObservationsOnFloorPlan();
        this.observationsList = this.filterObservations(
          floorObservation,
          viewPrivateObservationsOnFloorPlan,
          viewPublicObservationsOnFloorPlan,
        );
      }
      this.isLoading = false;
    }

    if (this.data.type === 'points') {
      const pointIds = this.data.points.map((point) => point.id);
      if (!this._project.isOperationEngineer()) {
        this.selectedReport = this._project.getReportById(this._project.selectedReport.id);
        let points = await this._project.getPointsImages();
        this.points = points
          .filter((point) => {
            if (this.isReportVideo()) {
              return (
                pointIds.includes(point.id) &&
                point.floor_id === this._project.selectedFloor.id &&
                point.report_id === this._project.selectedReport.id
              );
            }

            return (
              point.floor_id === this._project.selectedFloor.id &&
              point.report_id === this._project.selectedReport.id
            );
          })
          // Clone + scale instead of mutating — these point objects live in
          // the project service, so in-place mutation used to compound the
          // scaling factor on every popup re-open (points drifted toward 0,0
          // and shrank each time).
          .map((point) => ({
            ...point,
            point_x:
              point.point_x * (this.imgDim.realW / parseFloat(this.selectedFloor.image_width)),
            point_y:
              point.point_y * (this.imgDim.realH / parseFloat(this.selectedFloor.image_height)),
          }));
      } else {
        this.selectedReport = this._project.selectedReport;
        this.selectedFloor = this._project.selectedFloor;
        let reportFloor = this.selectedReport.floors.find(
          (floor) => floor.floor_id == this.selectedFloor.id,
        );
        // Clone + scale instead of mutating — video_points is shared report state.
        this.points = reportFloor.video_points.map((point) => ({
          ...point,
          point_x: point.point_x * (this.imgDim.realW / parseFloat(this.selectedFloor.image_width)),
          point_y:
            point.point_y * (this.imgDim.realH / parseFloat(this.selectedFloor.image_height)),
        }));
        // this.points = this.points.filter((point) => {
        //   return (
        //     pointIds.includes(point.id) &&
        //     point.floor_id === this._project.selectedFloor.id &&
        //     point.report_id === this._project.selectedReport.id
        //   );
        // });
      }
      this.isVideoReport = this.isReportVideo();
      this.checkLoader();
    }
  }

  filterObservations(
    floorObservation,
    viewPrivateObservationsOnFloorPlan,
    viewPublicObservationsOnFloorPlan,
  ): any {
    let observationsList = [];
    switch (true) {
      case viewPrivateObservationsOnFloorPlan && viewPublicObservationsOnFloorPlan:
        observationsList = floorObservation.filter(
          (t) => t.observation_flag === 'observation_on_floor_plan',
        );
        break;
      case viewPrivateObservationsOnFloorPlan:
        observationsList = floorObservation.filter(
          (t) => t.observation_flag === 'observation_on_floor_plan' && t.privacy === 'private',
        );
        break;
      case viewPublicObservationsOnFloorPlan:
        observationsList = floorObservation.filter(
          (t) => t.observation_flag === 'observation_on_floor_plan' && t.privacy === 'public',
        );
        break;
      default:
        observationsList = [];
    }
    return observationsList;
  }

  // ngOnChanges(changes: SimpleChanges): void {}

  checkMobile(): boolean {
    const ua = window.navigator.userAgent;
    if (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(ua)
    ) {
      return true;
    } else {
      return false;
    }
  }

  onMouseDown($event: MouseEvent): void {
    this.mousePos.x = $event.screenX;
    this.mousePos.y = $event.screenY;
  }

  checkFloorAndPoint() {
    if (this.selectedFloor.id !== this.data.currentPImg.floor_id) {
      const dialogData = new ConfirmDialogModel(
        this._transloco.translate('Alert'),
        this._transloco.translate(
          'No point selected. Observation will be linked to the floor only.',
        ),
        false,
        false,
        '',
        this._transloco.translate('Continue Anyway'),
        this._transloco.translate('Go Back'),
        '',
        false,
        [],
        '',
        false,
        'heroicons_solid:exclamation',
      );
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        maxWidth: '450px',
        minWidth: '300px',
        data: dialogData,
      });
      dialogRef.afterClosed().subscribe(async (dialogResult) => {
        if (!dialogResult) {
          this.canAddObservation = false;
        }
      });
    }
  }

  addPoint($event: MouseEvent): void {
    if (
      $event.currentTarget['localName'] === 'img' &&
      !($event.screenX >= this.mousePos.x - 1 && $event.screenX <= this.mousePos.x + 1) &&
      !($event.screenY >= this.mousePos.y - 1 && $event.screenY <= this.mousePos.y + 1)
    ) {
      return;
    }
    if ($event.button !== 0) {
      return;
    }
    if (this.canAddObservation) {
      const selectedReport = this._project.selectedReport;
      this.canAddObservation = false;
      let dialogRef;
      if (this.checkMobile()) {
        dialogRef = this.dialog.open(FloorObservationDetailsPopupComponent, {
          maxWidth: '100vw',
          maxHeight: '100vh',
          height: '100%',
          width: '100%',
          data: {
            type: 'ADD',
            is_observation_creation_enabled: this.selectedFloor.is_observation_creation_enabled,
            floor_id: this.selectedFloor.id,
            project_id: this.data.project.id,
            location_x:
              $event.offsetX / (this.imgDim.displayW / this.imgDim.realW) / this.ratioBtwImgs.w,
            location_y:
              $event.offsetY / (this.imgDim.displayH / this.imgDim.realH) / this.ratioBtwImgs.h,
            report_id: selectedReport.id,
            point_id:
              this.selectedFloor.id === this.data.currentPImg.floor_id
                ? this.data.currentPImg.point_id
                : null,
          },
          disableClose: true,
        });
      } else {
        dialogRef = this.dialog.open(FloorObservationDetailsPopupComponent, {
          width: '700px',
          data: {
            type: 'ADD',
            is_observation_creation_enabled: this.selectedFloor.is_observation_creation_enabled,
            floor_id: this.selectedFloor.id,
            project_id: this.data.project.id,
            location_x:
              $event.offsetX / (this.imgDim.displayW / this.imgDim.realW) / this.ratioBtwImgs.w,
            location_y:
              $event.offsetY / (this.imgDim.displayH / this.imgDim.realH) / this.ratioBtwImgs.h,
            report_id: selectedReport.id,
            point_id:
              this.selectedFloor.id === this.data.currentPImg.floor_id
                ? this.data.currentPImg.point_id
                : null,
          },
          disableClose: true,
        });
      }

      dialogRef.afterClosed().subscribe((result) => {
        GStatic.log('result', result);
        if (result && typeof result === 'object') {
          result.status = 'pending';
          if (result.title) {
            this._project
              .fetchFloorObservations(this._project.selectedFloor.id, this._project.id)
              .then((floorObservation) => {
                this.observationsList = floorObservation.filter(
                  (t) => t.observation_flag === 'observation_on_floor_plan',
                );
              });
          }
        }
      });
    }
  }

  openObservation(observation: any): void {
    let dialogRef;
    if (this.checkMobile()) {
      dialogRef = this.dialog.open(FloorObservationDetailsPopupComponent, {
        maxWidth: '100vw',
        maxHeight: '100vh',
        height: '100%',
        width: '100%',
        data: {
          type: 'VIEW',
          observation_id: observation.id,
          project_id: this.data.project.id,
        },
      });
    } else {
      dialogRef = this.dialog.open(FloorObservationDetailsPopupComponent, {
        width: '900px',
        data: {
          type: 'VIEW',
          observation_id: observation.id,
          project_id: this.data.project.id,
        },
      });
    }

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        const floorObservation = await this._project.fetchFloorObservations(
          this._project.selectedFloor.id,
          this._project.id,
        );
        const viewPrivateObservationsOnFloorPlan = this.getViewPrivateObservationsOnFloorPlan();
        const viewPublicObservationsOnFloorPlan = this.getViewPublicObservationsOnFloorPlan();
        this.observationsList = this.filterObservations(
          floorObservation,
          viewPrivateObservationsOnFloorPlan,
          viewPublicObservationsOnFloorPlan,
        );
      }
    });
  }

  fixDimension(): void {
    if (this.floorImgElm) {
      this.imgDim.displayH = this.floorImgElm.nativeElement.offsetHeight;
      this.imgDim.displayW = this.floorImgElm.nativeElement.offsetWidth;
    }
    if (this.imgContainer) {
      this.contDim.displayH = this.imgContainer.nativeElement.offsetHeight;
      this.contDim.displayW = this.imgContainer.nativeElement.offsetWidth;
    }
  }

  onScaleSlider($event: MatSliderChange): void {
    if (this.isAdjustmentMode) {
      return;
    }
    this.changeImgSize($event.value);
  }

  onScaleSliderAdjustment($event: any): void {
    if (!this.isAdjustmentMode) {
      return;
    }
    this.changeImgSize($event);
  }

  zoom(amount: 'in' | 'out'): void {
    this.stopAnimation();
    if (amount === 'in') {
      this.googleServices.setEvent('OpenFloorObservations_ZoomIn_projects', 'Interactivity');
      this.imgScale = this.imgScale < 400 ? this.imgScale + 20 : 400;
      this.changeImgSize(this.imgScale);
    } else {
      this.googleServices.setEvent('OpenFloorObservations_ZoomOut_projects', 'Interactivity');
      this.imgScale =
        this.imgScale > this.imgScaleDefault ? this.imgScale - 20 : this.imgScaleDefault;
      this.changeImgSize(this.imgScale);
    }
  }

  resetImgSizeAndPosition(): void {
    this.prevImgDim = structuredClone(this.imgDim);
    this.googleServices.setEvent('OpenFloorObservations_ResetPosition_projects', 'Interactivity');
    const ratioH = this.contDim.displayH / this.imgDim.realH;
    const ratioW = this.contDim.displayW / this.imgDim.realW;

    this.imgScale = ratioH < ratioW ? ratioH * 100 : ratioW * 100;
    this.imgDim.displayH = (this.imgDim.realH * this.imgScale) / 100;
    this.imgDim.displayW = (this.imgDim.realW * this.imgScale) / 100;

    this.imgDim.ratioH = this.imgDim.displayH / this.imgDim.realH;
    this.imgDim.ratioW = this.imgDim.displayW / this.imgDim.realW;

    this.dragPosition = {
      x: this.contDim.displayW / 2,
      y: this.contDim.displayH / 2,
    };

    this.isAdjustmentMode ? this.updatePathPoints() : null;
  }

  resetImgPosition(): void {
    this.imgDim.displayH = (this.imgDim.realH * this.imgScale) / 100;
    this.imgDim.displayW = (this.imgDim.realW * this.imgScale) / 100;

    this.imgDim.ratioH = this.imgDim.displayH / this.imgDim.realH;
    this.imgDim.ratioW = this.imgDim.displayW / this.imgDim.realW;

    const translate = this.dragableImage.nativeElement.style.transform
      .replace('translate3d(', '')
      .replace(')', '')
      .split('px')
      .join('')
      .split(',');
    const ox = Number(translate[0]);
    const oy = Number(translate[1]);
    const startVal = { x: ox, y: oy };
    const endVal = {
      x: this.contDim.displayW / 2,
      y: this.contDim.displayH / 2,
    };

    const calculatePos = (perc: number, a: number, b: number) => {
      return perc * (b - a) + a;
    };

    this.animate(
      0,
      1,
      (val) => {
        this.dragPosition = {
          x: calculatePos(val, startVal.x, endVal.x),
          y: calculatePos(val, startVal.y, endVal.y),
        };
      },
      600,
    );
  }

  changeImgSize(value: number): void {
    this.prevImgDim = structuredClone(this.imgDim);
    this.imgDim.ratioH = this.imgDim.displayH / this.imgDim.realH;
    this.imgDim.ratioW = this.imgDim.displayW / this.imgDim.realW;
    this.imgDim.displayH = (this.imgDim.realH * value) / 100;
    this.imgDim.displayW = (this.imgDim.realW * value) / 100;

    this.fixBoundary();
    this.isAdjustmentMode ? this.updatePathPoints() : null;
  }

  fixBoundary(): void {
    this.stopAnimation();
    const margin = 0;
    const fixedX = this.imgDim.displayW / 2;
    const fixedY = this.imgDim.displayH / 2;
    const translate = this.dragableImage.nativeElement.style.transform
      .replace('translate3d(', '')
      .replace(')', '')
      .split('px')
      .join('')
      .split(',');
    const ox = Number(translate[0]);
    const oy = Number(translate[1]);
    let bx = ox - margin;
    let by = oy - margin;

    if (this.contDim.displayW > this.imgDim.displayW) {
      if (ox < +margin + fixedX) {
        // 1

        bx = fixedX;
      }
      if (ox + this.imgDim.displayW - margin > this.contDim.displayW + fixedX) {
        // 2
        bx = this.contDim.displayW - this.imgDim.displayW + fixedX;
      }
    } else {
      if (ox > +margin + fixedX) {
        // 3
        bx = fixedX;
      }
      if (ox + this.imgDim.displayW - margin < this.contDim.displayW + fixedX) {
        // 4
        bx = this.contDim.displayW - this.imgDim.displayW + fixedX;
      }
    }
    if (this.contDim.displayH > this.imgDim.displayH) {
      if (oy < +24 + fixedY) {
        // 5
        by = fixedY;
      }
      if (oy + this.imgDim.displayH - margin > this.contDim.displayH + fixedY) {
        // 6
        by = this.contDim.displayH - this.imgDim.displayH + fixedY;
      }
    } else {
      if (oy > +margin + fixedY) {
        // 7
        by = fixedY;
      }
      if (oy + this.imgDim.displayH - margin < this.contDim.displayH + fixedY) {
        // 8
        by = this.contDim.displayH - this.imgDim.displayH + fixedY;
      }
    }

    this.dragPosition = { x: bx, y: by };
    if (
      this.contDim.displayH > this.imgDim.displayH &&
      this.contDim.displayW > this.imgDim.displayW
    ) {
      this.timerInterval[1] = setTimeout(() => {
        this.prevImgDim = this.imgDim;
        this.resetImgPosition();
        this.isAdjustmentMode ? this.updatePathPoints() : null;
      }, 300);
    }
  }

  stopAnimation(): void {
    if (this.timerInterval[0]) {
      clearInterval(this.timerInterval[0]);
    }
    if (this.timerInterval[1]) {
      clearInterval(this.timerInterval[1]);
    }
  }

  animate(
    from: number = 0,
    to: number = 300,
    fun: (x: number) => void,
    duration: number = 500,
  ): ReturnType<typeof setTimeout> {
    clearInterval(this.timerInterval[0]);
    const easeInOutQuart = (t, b, c, d) => {
      // eslint-disable-next-line no-cond-assign
      if ((t /= d / 2) < 1) {
        return (c / 2) * t * t * t * t + b;
      }
      return (-c / 2) * ((t -= 2) * t * t * t - 2) + b;
    };

    const start = new Date().getTime();
    this.timerInterval[0] = setInterval(() => {
      const time = new Date().getTime() - start;
      const x = easeInOutQuart(time, from, to - from, duration);
      fun(x);
      if (time >= duration) {
        clearInterval(this.timerInterval[0]);
        fun(to);
      }
    }, 1000 / 60);

    return this.timerInterval[0];
  }

  getAddObservationOnFloorPlanPermission(): boolean {
    return this._project.getPermission(AccessLevel.CREATE_NEW_SNAG);
  }

  getViewPrivateObservationsOnFloorPlan(): boolean {
    return this._project.getPermission(AccessLevel.VIEW_PRIVATE_SNAGS);
  }

  getViewPublicObservationsOnFloorPlan(): boolean {
    return this._project.getPermission(AccessLevel.VIEW_PUBLIC_SNAGS);
  }

  async changePoint(point: PointImage): Promise<void> {
    const mappedPoint: ProjectFloorPoint = {
      id: point.point_id,
      lat: point.lat,
      lng: point.lng,
      point_name: point.point_name,
      point_x: point.point_x,
      point_y: point.point_y,
      is_selected_point: point.is_selected_point,
      map_point_radius: point.map_point_radius,
      bim_point_x: parseFloat(point.bim_point_x),
      bim_point_y: parseFloat(point.bim_point_y),
      bim_point_z: parseFloat(point.bim_point_z),
    };
    await this._project.initCurrentPImg(point.id, 0);
    this._pano.loadImage(this._pano.mainPano.id, point.image_360, null, 115, mappedPoint);
    this._navigationService.selectedPoint = mappedPoint;
    // this._pano.mainPano.set('view.hlookat', point.horizontal_angle);
    // this._pano.mainPano.set('view.vlookat', point.vertical_angle);
    //
    this._pano.map_activePoint(mappedPoint);
    this.dialogRef.close();
  }

  isReportVideo(): boolean {
    if (!this._project.isOperationEngineer())
      return this.selectedReport.report_type === REPORT_TYPE.VIDEOS;
    else return true;
  }

  // temp code for operation manager
  activateAdjustMode() {
    this.isAdjustmentMode = true;
    this.startAdjustment();
  }

  cancelAdjustments() {
    this.isAdjustmentMode = false;
  }

  confirmFloor() {
    const dialogData = new ConfirmDialogModel(
      this._transloco.translate(
        this.isAdjustmentMode
          ? 'Do you want to save the changes made to the path? '
          : 'Are you sure you want to save the path without making any adjustments? ',
      ),
      null,
      false,
      false,
      '',
      this._transloco.translate('Save'),
    );
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      maxWidth: '450px',
      minWidth: '300px',
      data: dialogData,
    });
    dialogRef.afterClosed().subscribe(async (dialogResult) => {
      if (dialogResult) {
        if (this.isAdjustmentMode) {
          this.isAdjustmentMode = false;
          const ratioW = this.ratioBtwImgs?.w || 1;
          const ratioH = this.ratioBtwImgs?.h || 1;
          const updatedPoints = this.adjustPathObj.getPoints();

          // Keep the local render copy in canvas (realW) space so the pipe
          // still renders correctly if change detection fires before close.
          const canvasSpacePoints = updatedPoints.map((point) => ({
            ...point,
            point_x: (point.point_x * this.imgDim.realW) / (this.imgDim.displayW * ratioW),
            point_y: (point.point_y * this.imgDim.realH) / (this.imgDim.displayH * ratioH),
          }));
          this.points = canvasSpacePoints;

          // Emit in the floor image's ORIGINAL coordinate space — the minimap
          // (prepareMap / map_addPoint) and sessionStorage consumers expect
          // original-image coords, not the 1080-canvas coords used locally.
          const origW = parseFloat(this.selectedFloor.image_width);
          const origH = parseFloat(this.selectedFloor.image_height);
          const emittedPoints = canvasSpacePoints.map((point) => ({
            ...point,
            point_x: (point.point_x * origW) / this.imgDim.realW,
            point_y: (point.point_y * origH) / this.imgDim.realH,
          }));

          this.pendingReportsService.adjustedFloor.next({
            floorId: this.selectedFloor.id,
            points: emittedPoints,
            reportId: this._project.selectedReport.id,
          });
        } else {
          let floorIndex = this._project.floors.findIndex(
            (floor) => floor.id == this.selectedFloor.id,
          );
          this._project.floors[floorIndex].isChecked = true;
        }

        this.dialogRef.close();
      }
    });
  }

  async prepareImage(): Promise<void> {
    this.selectedFloor = this._project.floors.find((floor) => {
      return floor.id === this._project.selectedFloor.id;
    });
    const file = this.selectedFloor.floor_image_hq || this.selectedFloor.floor_image;

    await this.resizeImageToFitBox(file).then(async (result) => {
      const image = result.resizedImg;
      console.log('After treatment', image.width, image.height);
      if (image) {
        this.floorImg = image.src;
      }

      this.contDim.displayH = this.imgContainer.nativeElement.offsetHeight;
      this.contDim.displayW = this.imgContainer.nativeElement.offsetWidth;

      this.imgDim.realH = image.height;
      this.imgDim.realW = image.width;

      //using only one size of image
      this.ratioBtwImgs = {
        w: 1,
        h: 1,
      };

      this.resetImgSizeAndPosition();
      /*Get this value to setup default minimum zoom value for the floor image*/
      this.imgScaleDefault = this.getDefaultImageScale();
    });
  }

  resizeImageToFitBox(imageUrl: string, maxWidth = 1080, maxHeight = 1080): Promise<any> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous'; // Important for CORS images

      img.onload = () => {
        console.log('Before treatment ', img.width, img.height);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        const { width: originalWidth, height: originalHeight } = img;

        // Calculate scaling while preserving aspect ratio
        let targetWidth = originalWidth;
        let targetHeight = originalHeight;

        const widthRatio = maxWidth / originalWidth;
        const heightRatio = maxHeight / originalHeight;
        const ratio = Math.min(widthRatio, heightRatio, 1); // don't upscale

        targetWidth = originalWidth * ratio;
        targetHeight = originalHeight * ratio;

        canvas.width = targetWidth;
        canvas.height = targetHeight;

        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

        const resizedImg = new Image();
        resizedImg.onload = () =>
          resolve({ resizedImg, original: { width: originalWidth, height: originalHeight } });
        resizedImg.onerror = (err) => reject(err);
        resizedImg.src = canvas.toDataURL('image/png'); // or 'image/jpeg' if needed
      };

      img.onerror = (err) => reject(err);
      img.src = imageUrl;
    });
  }

  startAdjustment() {
    setTimeout(() => {
      const ratioW = this.ratioBtwImgs?.w || 1;
      const ratioH = this.ratioBtwImgs?.h || 1;
      // Project canvas-space points into display space for the AdjustPath overlay.
      // Spread gives AdjustPath its own point instances, so no structuredClone needed.
      const displaySpacePoints = this.points.map((point) => ({
        ...point,
        point_x: ((point.point_x * this.imgDim.displayW) / this.imgDim.realW) * ratioW,
        point_y: ((point.point_y * this.imgDim.displayH) / this.imgDim.realH) * ratioH,
      }));
      this.adjustPathObj = new AdjustPath(displaySpacePoints, this.scriptloader);
    }, 500);
  }

  updatePathPoints() {
    if (this.prevImgDim === this.imgDim) {
      return;
    }
    const ratioW = this.ratioBtwImgs?.w || 1;
    const ratioH = this.ratioBtwImgs?.h || 1;
    // Re-project points through the new imgDim on zoom/resize:
    // prev-display → canvas (prev realW) → new-display. Single pass, no rounding.
    const rescaled = this.adjustPathObj.getPoints().map((point) => {
      const canvasX = (point.point_x * this.prevImgDim.realW) / (this.prevImgDim.displayW * ratioW);
      const canvasY = (point.point_y * this.prevImgDim.realH) / (this.prevImgDim.displayH * ratioH);
      return {
        ...point,
        point_x: ((canvasX * this.imgDim.displayW) / this.imgDim.realW) * ratioW,
        point_y: ((canvasY * this.imgDim.displayH) / this.imgDim.realH) * ratioH,
      };
    });

    this.adjustPathObj.originalPoints = rescaled;
    this.adjustPathObj.reInitialize();
  }

  private getDefaultImageScale() {
    const ratioH = this.contDim.displayH / this.imgDim.realH;
    const ratioW = this.contDim.displayW / this.imgDim.realW;
    return ratioH < ratioW ? ratioH * 100 : ratioW * 100;
  }

  checkLoader() {
    // Check if points are rendered if yes then close loading and clear interval
    this.isLoading = true;
    let loadingPoints = setInterval(() => {
      if (this.pointList.nativeElement?.children?.length === this.points?.length) {
        this.isLoading = false;
        clearInterval(loadingPoints);
      }
    }, 1000);
  }

  openRejectFloorPopup() {
    let italicMessage = this._transloco.translate(
      "Once rejected, you won't be able to re-adjust this floor . Please make sure you're certain before proceeding.",
    );

    let title = this._transloco.translate('Are you sure you want to reject this floor?');
    const dialogData = new ConfirmDialogModel(title, null, true, true, italicMessage, 'Reject');
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      maxWidth: '450px',
      minWidth: '300px',
      data: dialogData,
    });
    dialogRef.afterClosed().subscribe(async (dialogResult) => {
      if (dialogResult) {
        this.rejectFloor(dialogRef);
      }
    });
  }

  rejectFloor(dialog: MatDialogRef<ConfirmDialogComponent>): void {
    const reportFloors: string[] = [];
    reportFloors.push(this.selectedFloor.id);
    this.pendingReportsService.rejectPendingReport(this.selectedReport.id, reportFloors).then(
      async (res: any) => {
        if (res) {
          this._toaster.success(this._transloco.translate('Floor got rejected successfully'));
          this.dialogRef.close();
        }
      },
      (error) => {
        this._toaster.error(error.error?.message);
      },
    );
  }

  getStatusForIcon(status: string): string {
    return statusToIconKey(status);
  }

  getObservationIcon(observation: any): string {
    if (observation?.type === 'note') {
      return '/assets/krpano/media/note.png';
    }
    const status = this.getStatusForIcon(observation?.status);
    return `/assets/krpano/media/alert-${status}.png`;
  }
}
